// /hello — sample listener route + M3.2-P4 audit-fields test buttons.
// -----------------------------------------------------------------
// Per docs/architecture/m3-spa-scaffold-plan.md § 2.7 + dispatch
// m3-2-auth-rules-listener.md § 6.10 + § 5.4 (P4) + § 4.4.
//
// Lists every `tours_staging` document title via a live
// `useFirestoreCollection` listener. Per audit § 8.3.3, this is the
// canonical SPA-substrate-win demo: the lead writes a doc to
// `tours_staging` via Firebase Console (or via the existing Flask
// admin), and the list updates here within ~1s without a page
// refresh. M4-M6 routes adopt the same pattern.
//
// Below the list, two M3.2-P4 audit-fields test buttons:
//   1. "Test write WITH audit fields" — uses `withAuditFields()`,
//      should land successfully once rules are deployed at P3.
//   2. "Test write WITHOUT audit fields" — bare `setDoc`, should
//      be rejected with `permission-denied` once rules are
//      deployed at P3 (succeeds today against pre-deploy rules,
//      which is exactly the regression these buttons let the lead
//      observe).
//
// The buttons are gated behind `import.meta.env.DEV` OR the
// `VITE_FF_AUDIT_FIELDS_TEST` feature flag. They MUST NOT ship to
// prod. M3.4-L3 (first prod SPA deploy) will run with `DEV=false`
// and `VITE_FF_AUDIT_FIELDS_TEST` unset in `.env.production`, so
// the buttons stay hidden. Verified at P4 build time.

import {
  Alert,
  Button,
  Card,
  Group,
  List,
  Loader,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createFileRoute } from '@tanstack/react-router';
import { setDoc, doc } from 'firebase/firestore';
import { useState } from 'react';

import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { auditedSet } from '@/lib/audit';
import { isFeatureEnabled } from '@/lib/featureFlags';
import { db } from '@/lib/firebase';

type TourStagingDoc = {
  name?: string;
  localized?: { fr?: { title?: string }; en?: { title?: string } };
};

// --- M3.2-P4 audit-fields test surface ---------------------------
// Combined gate: DEV mode (npm run dev) OR explicit feature flag
// for the deployed staging environment. .env.staging sets
// VITE_FF_AUDIT_FIELDS_TEST=on so the lead can exercise the rule
// at P4 against staging-admin.tech4art.info; .env.production omits
// it so the buttons never render in prod.
const SHOW_AUDIT_TEST_BUTTONS =
  import.meta.env.DEV || isFeatureEnabled('AUDIT_FIELDS_TEST');

// The 4 audit-collections still gated by hasAuditFields() in firestore.rules
// (#541: the world-readable tours_private / artworks_private / settings
// use the audit-ref format and are NOT offered — a test write there would
// land in live public data plus an immutable catalog_audit entry)
// (per docs/architecture/m3-spa-scaffold-plan.md § 2.6 + dispatch § 3.3).
// Lead picks any of these from the dropdown in the P4 test card to
// exercise the rule on the corresponding match block. Firebase Console
// writes can NOT substitute for these tests because Console writes go
// through Admin SDK, which bypasses rules entirely — the SPA is the only
// rules-bound client surface available for P4 manual validation.
const AUDIT_COLLECTIONS = [
  'tours_plans',
  'tours_staging',
  'artworks_staging',
  'artworks_production_backups',
] as const;

type AuditCollection = (typeof AUDIT_COLLECTIONS)[number];

// Stable random-ish doc id for test writes; we don't want to
// pollute the collection with hundreds of docs across a P4 run, so
// we time-bucket it by the run minute. The lead can prune via
// Firebase Console after P4.
const makeTestDocId = (): string =>
  `m3-2-p4-test-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const renderTestButtons = () => {
  return <AuditTestButtons />;
};

const AuditTestButtons = () => {
  const [submitting, setSubmitting] = useState<'with' | 'without' | null>(null);
  const [collection, setCollection] = useState<AuditCollection>('tours_plans');

  const writeWithAuditFields = async () => {
    setSubmitting('with');
    const id = makeTestDocId();
    try {
      await auditedSet(
        doc(db, collection, id),
        { name: `M3.2-P4 test (with audit) ${id}` },
        'hello',
      );
      notifications.show({
        color: 'green',
        title: 'Write succeeded',
        message: `${collection}/${id} created with audit fields.`,
      });
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'permission-denied') {
        notifications.show({
          color: 'red',
          title: 'Permission denied (UNEXPECTED for WITH-fields path)',
          message: `${collection}: rule rejected an audited write. Possible rule-shape regression — surface to lead immediately.`,
        });
      } else {
        notifications.show({
          color: 'red',
          title: 'Write failed (unexpected)',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    } finally {
      setSubmitting(null);
    }
  };

  const writeWithoutAuditFields = async () => {
    setSubmitting('without');
    const id = makeTestDocId();
    try {
      // Bare write — no withAuditFields(). After P3, the rule
      // rejects this with `permission-denied`. Before P3, this
      // succeeds (which is the regression the lead can observe to
      // verify the rule actually changed behaviour).
      await setDoc(doc(db, collection, id), {
        name: `M3.2-P4 test (no audit) ${id}`,
      });
      notifications.show({
        color: 'orange',
        title: 'Write succeeded (rule not yet enforced?)',
        message: `${collection}/${id} created WITHOUT audit fields. If rules are deployed, this is a regression.`,
      });
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'permission-denied') {
        notifications.show({
          color: 'green',
          title: 'Permission denied (expected)',
          message: `${collection}: audit-fields rule rejected the write — exactly as designed.`,
        });
      } else {
        notifications.show({
          color: 'red',
          title: 'Write failed (unexpected error)',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <Card withBorder shadow='xs' padding='md' radius='md' mt='lg'>
      <Stack>
        <Title order={4}>M3.2-P4 audit-fields rule test</Title>
        <Text size='sm' c='dimmed'>
          Pick any of the 4 audit-collections. After the rules deploy at
          M3.2-P3, the WITHOUT button should fail with{' '}
          <code>permission-denied</code>; the WITH button should succeed. Hidden
          in prod (gated on DEV mode or
          <code> VITE_FF_AUDIT_FIELDS_TEST</code>). Note: Firebase Console
          writes go through Admin SDK and bypass rules, so they CANNOT
          substitute for these SPA-driven tests — only the SPA exercises the
          live rule predicate.
        </Text>
        <Select
          label='Target audit-collection'
          value={collection}
          onChange={(v) => v && setCollection(v as AuditCollection)}
          data={AUDIT_COLLECTIONS.map((c) => ({ value: c, label: c }))}
          allowDeselect={false}
        />
        <Group>
          <Button
            onClick={() => void writeWithAuditFields()}
            loading={submitting === 'with'}
            color='green'
            variant='light'
          >
            Test write WITH audit fields
          </Button>
          <Button
            onClick={() => void writeWithoutAuditFields()}
            loading={submitting === 'without'}
            color='red'
            variant='light'
          >
            Test write WITHOUT audit fields
          </Button>
        </Group>
      </Stack>
    </Card>
  );
};

const HelloRoute = () => {
  const {
    data: tours,
    loading,
    error,
  } = useFirestoreCollection<TourStagingDoc>('tours_staging');

  return (
    <Stack>
      <Title order={2}>Staging tours (live listener)</Title>
      <Text size='sm' c='dimmed'>
        Subscribed to <code>tours_staging</code> via <code>onSnapshot</code>.
        Adds, updates, and deletes appear here within ~1s without a page
        refresh.
      </Text>

      {loading && <Loader size='sm' />}

      {error && (
        <Alert color='red' variant='light' title='Listener error'>
          {error.message}
        </Alert>
      )}

      {!loading && !error && tours.length === 0 && (
        <Text c='dimmed'>(no tours in tours_staging)</Text>
      )}

      {!loading && !error && tours.length > 0 && (
        <List>
          {tours.map((t) => {
            const title =
              t.localized?.fr?.title ??
              t.localized?.en?.title ??
              t.name ??
              '(untitled)';
            return (
              <List.Item key={t.id}>
                {title}{' '}
                <Text component='span' size='xs' c='dimmed'>
                  ({t.id})
                </Text>
              </List.Item>
            );
          })}
        </List>
      )}

      {SHOW_AUDIT_TEST_BUTTONS && renderTestButtons()}
    </Stack>
  );
};

export const Route = createFileRoute('/_authenticated/hello')({
  component: HelloRoute,
});
